// src/main/resources/web/app/chart-config.js

// ============================================================================
// Constants
// ============================================================================

export const DEFAULT_TIME_RANGE = '1W';
export const SYMBOLS_TO_IGNORE = ["NEXO", "LEO", "XAUt", "KCS", "OKB", "GT", "CRO", "BGB", "PAXG"];
export const TIME_RANGES = {
    '1D': 24 * 60 * 60 * 1000,
    '1W': 7 * 24 * 60 * 60 * 1000,
    '1M': 30 * 24 * 60 * 60 * 1000,
    '3M': 3 * 30 * 24 * 60 * 60 * 1000,
    '1Y': 365 * 24 * 60 * 60 * 1000,
    'All': null
};
export const SCORE_THRESHOLD = {
    bottom_percent: 1 / 3,
    top_percent: 2 / 3,
    global_bottom_percent: 0.1,
    global_top_percent: 0.9,
};

// ============================================================================
// Color Palette
// ============================================================================

export const PALETTE = {
    btcDominance: '#3b82f6', // blue-500
    altcoinDominance: '#ef4444', // red-500
    dominanceDifference: '#9333ea', // purple-600
    btcToTop10AltDominanceLogRatio: '#f97316', // orange-500
    hhi: '#f59e0b', // amber-500
    chartBackground: '#1f2937',
    chartText: 'rgba(255, 255, 255, 0.9)',
    chartGrid: 'rgba(255, 255, 255, 0.1)',
    markerHigh: '#22c55e', // green-500
    markerLow: '#ef4444', // red-500
    rangePositive: '#10b981', // emerald-500
    rangeNegative: '#f43f5e', // rose-500
};

export const COLD_COLOR_RGB = [12, 10, 62]; // Deep Indigo
export const HOT_COLOR_RGB = [254, 249, 195]; // Yellow-100

// ============================================================================
// Chart and Series Options
// ============================================================================

const commonChartOptions = {
    layout: {
        background: {color: PALETTE.chartBackground},
        textColor: PALETTE.chartText,
    },
    grid: {
        vertLines: {color: PALETTE.chartGrid},
        horzLines: {color: PALETTE.chartGrid},
    },
    rightPriceScale: {
        borderColor: PALETTE.chartGrid,
        visible: true,
    },
    timeScale: {
        borderColor: PALETTE.chartGrid,
        timeVisible: true,
        secondsVisible: false,
    },
    autoSize: true,
    handleScroll: true,
    handleScale: true,
    crosshair: {
        mode: 0, // CrosshairMode.Normal
    },
    legend: {
        position: 'top_right',
        textColor: PALETTE.chartText,
        fontSize: 12,
    },
};

export const mainChartOptions = {
    ...commonChartOptions,
    leftPriceScale: {
        borderColor: PALETTE.chartGrid,
        visible: true,
    },
    timeScale: {
        ...commonChartOptions.timeScale,
        tickMarkFormatter: (time, tickMarkType, locale) => {
            const m = moment.utc(time * 1000);
            switch (tickMarkType) {
                case LightweightCharts.TickMarkType.Year:
                    return m.format('YYYY');
                case LightweightCharts.TickMarkType.Month:
                    return m.format('MMM YYYY');
                case LightweightCharts.TickMarkType.DayOfMonth:
                    return m.format('D MMM');
                case LightweightCharts.TickMarkType.Time:
                    return m.format('HH:mm');
                case LightweightCharts.TickMarkType.TimeWithSeconds:
                    return m.format('HH:mm:ss');
                default:
                    return m.format('YYYY-MM-DD HH:mm');
            }
        },
    },
};

export const giniChartOptions = {
    ...commonChartOptions,
    height: 300,
    leftPriceScale: {
        visible: false,
    },
    timeScale: {
        ...commonChartOptions.timeScale,
        tickMarkFormatter: mainChartOptions.timeScale.tickMarkFormatter, // Reuse formatter
    },
};

export const seriesOptions = {
    btcDominance: {
        priceScaleId: 'left',
        color: PALETTE.btcDominance,
        lineWidth: 2,
        title: 'BTC Dominance'
    },
    altcoinDominance: {
        priceScaleId: 'right',
        color: PALETTE.altcoinDominance,
        lineWidth: 2,
        title: 'Altcoin Dominance'
    },
    dominanceDifference: {
        priceScaleId: 'left',
        color: PALETTE.dominanceDifference,
        lineWidth: 2,
        title: 'Dominance Difference'
    },
    btcToTop10AltDominanceLogRatio: {
        priceScaleId: 'right',
        color: PALETTE.btcToTop10AltDominanceLogRatio,
        lineWidth: 2,
        title: 'BTC to Top 10 Alt Dominance Log Ratio'
    },
    hhi: {
        priceScaleId: 'right',
        color: PALETTE.hhi,
        lineWidth: 2,
        title: 'Gini Alt Excl Top 10 Stable'
    }
};

// ============================================================================
// Application State
// ============================================================================

export const state = {
    chart: null,
    giniChart: null,
    overlay: null,
    positivePctChangeTimeRange: { startTime: null, endTime: null, duration: null },
    negativePctChangeTimeRange: { startTime: null, endTime: null, duration: null },
    rangeMarkers: [],
    dominanceDifferenceMarkers: null,
    extremeTimestamps: [],
    extremeMarkersEnabled: true,
    currentViewMode: 'aggr',
    selectedTimezone: 'Etc/UTC', // Default, will be updated on load
    columnThresholds: {},
};
